/**
 * Min/max admin API client — export/import per location + copy between
 * smartboxes. Platform + vendor-admin only (enforced server-side). All writes
 * land in _jcox.mttr.workcenter_item_minmax_override (never the WMS).
 */

import { ApiError, apiFetch } from "./apiClient";

export interface MinMaxRow {
  item_id: number;
  sku: string | null;
  name: string | null;
  wms_min: number | null;
  wms_suggest: number | null;
  override_min: number | null;
  override_suggest: number | null;
  effective_min: number | null;
  effective_suggest: number | null;
  has_override: boolean;
}

export interface MinMaxExport {
  workcenter: string;
  workcenter_id: number;
  rows: MinMaxRow[];
}

export interface ImportRow {
  item_id: number;
  min_qty: number | null;
  suggest_qty: number | null;
}

export interface ImportResult {
  workcenter: string;
  applied: number;
  cleared: number;
  skipped: number[];
}

export interface CopyResult {
  source_scu: string;
  results: { scu_name: string; applied?: number; error?: string; note?: string }[];
}

export class MinMaxApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "MinMaxApiError";
  }
}

async function request<T>(path: string, token: string, init?: RequestInit): Promise<T> {
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new MinMaxApiError(e.message, e.status) : e;
  }
}

/** Current effective + WMS + override min/max for a location (for CSV export). */
export function fetchWorkcenterMinmax(workcenter: string, token: string): Promise<MinMaxExport> {
  return request<MinMaxExport>(`/api/minmax?workcenter=${encodeURIComponent(workcenter)}`, token);
}

/** Apply min/max override updates to one location. Blank min+max clears the
 *  override (reverts to WMS). */
export function importMinmax(
  workcenter: string,
  rows: ImportRow[],
  token: string,
): Promise<ImportResult> {
  return request<ImportResult>(`/api/minmax/import`, token, {
    method: "POST",
    body: JSON.stringify({ workcenter, rows }),
  });
}

/** Copy a source smartbox's effective min/max to one or more target smartboxes. */
export function copyMinmax(
  sourceScu: string,
  targetScus: string[],
  token: string,
): Promise<CopyResult> {
  return request<CopyResult>(`/api/minmax/copy`, token, {
    method: "POST",
    body: JSON.stringify({ source_scu: sourceScu, target_scus: targetScus }),
  });
}
